import { useQuery, useMutation } from '@apollo/client';
import {
  Box,
  Thead,
  Th,
  Tr,
  Table,
  Td,
  Tbody,
  Flex,
  Badge,
  TableContainer,
  Spinner,
  Button,
  Icon,
  Text,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
} from '@chakra-ui/react';
import { SortAscending, SortDescending, Equals } from '@phosphor-icons/react';
import { useState, useContext } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import get from 'lodash/get';

import { formatUTCToLocal } from '../../../utils';
import {
  GET_ACCESS_POINT_OVERVIEW,
  INTERNAL_SILENT_TOGGLE_AP_STATUS,
  INTERNAL_EXTEND_BLACKOUT_SCHEDULE,
  InternalAccessPointOverviewQuery,
  AccessPointOverviewSortField,
  AccessPointDisplayStatus,
  AccessPointOverviewEntry,
} from './query';
import { Pagination } from '../../../components/Pagination';
import { FilterSelectPill } from '../../../components/FilterPill/Select';
import Input from '../../../components/Input';
import UserContext from '../../../contexts/UserContext';
import { useCustomToast } from '../../../hooks/useCustomToast';
import { useSpinner } from '../../../contexts/SpinnerContext';

const CommentSchema = Yup.object().shape({
  comment: Yup.string().required('Du musst einen Kommentar/Begründung eingeben'),
});

const statusBadge = (status: AccessPointDisplayStatus) => {
  switch (status) {
    case 'ACTIVE':
      return (
        <Badge colorScheme="green" fontSize="xs">
          Aktiv
        </Badge>
      );
    case 'INACTIVE':
      return (
        <Badge colorScheme="orange" fontSize="xs">
          Inaktiv
        </Badge>
      );
    case 'CANCELLED':
      return (
        <Badge colorScheme="red" fontSize="xs">
          Gekündigt
        </Badge>
      );
  }
};

interface ActionModalProps {
  isOpen: boolean;
  onClose: () => void;
  refetch: () => void;
  entry: AccessPointOverviewEntry | null;
  actionType: 'activate' | 'deactivate' | 'extendBlackout';
}

const ActionModal = ({ isOpen, onClose, refetch, entry, actionType }: ActionModalProps) => {
  const [toggleActiveStatus] = useMutation(INTERNAL_SILENT_TOGGLE_AP_STATUS);
  const [extendBlackout] = useMutation(INTERNAL_EXTEND_BLACKOUT_SCHEDULE);
  const { user } = useContext(UserContext);
  const toast = useCustomToast();
  const { toggleSpinner } = useSpinner();

  if (!entry) return null;

  const configs = {
    activate: {
      title: 'Access Point aktivieren',
      description: `Möchtest du den Access Point "${entry.locationName || entry.accessPointID}" wirklich aktivieren?`,
      successMessage: 'Der Access Point wurde erfolgreich aktiviert.',
    },
    deactivate: {
      title: 'Access Point pausieren',
      description: `Möchtest du den Access Point "${entry.locationName || entry.accessPointID}" wirklich pausieren?`,
      successMessage: 'Der Access Point wurde erfolgreich pausiert.',
    },
    extendBlackout: {
      title: 'Blackout um 3 Monate verlängern',
      description: `Möchtest du den Blackout-Zeitraum für "${entry.locationName || entry.accessPointID}" um 3 Monate verlängern?`,
      successMessage: 'Der Blackout-Zeitraum wurde erfolgreich verlängert.',
    },
  };

  const config = configs[actionType];

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{config.title}</ModalHeader>
        <Formik
          initialValues={{ comment: '' }}
          validationSchema={CommentSchema}
          onSubmit={async ({ comment }) => {
            try {
              toggleSpinner(true);
              onClose();

              if (actionType === 'extendBlackout') {
                await extendBlackout({
                  variables: {
                    accessPointID: entry.accessPointID,
                    internalUserId: user.id,
                    comment,
                  },
                });
              } else {
                if (!entry.dropPointId) {
                  throw new Error('DropPoint ID fehlt für diesen Access Point.');
                }
                await toggleActiveStatus({
                  variables: {
                    stationId: entry.dropPointId,
                    active: actionType === 'activate',
                    internalUserId: user.id,
                    comment,
                  },
                });
              }

              await refetch();
              toast({
                title: 'Erfolg!',
                description: config.successMessage,
                status: 'success',
              });
            } catch (error) {
              console.log(error);
              toast({
                description: get(error, 'graphQLErrors.0.message', 'Ein Fehler ist aufgetreten!'),
                status: 'error',
              });
            } finally {
              toggleSpinner(false);
            }
          }}
        >
          {() => (
            <Form>
              <ModalBody>
                <Text fontSize="sm" mb="4">
                  {config.description}
                </Text>
                <Input name="comment" label="Kommentar/Begründung" />
              </ModalBody>
              <ModalFooter>
                <Button variant="ghost" size="sm" colorScheme="red" type="button" onClick={onClose}>
                  Abbrechen
                </Button>
                <Button ml="4" size="sm" colorScheme="brand" type="submit">
                  Bestätigen
                </Button>
              </ModalFooter>
            </Form>
          )}
        </Formik>
        <ModalCloseButton />
      </ModalContent>
    </Modal>
  );
};

export const AccessPointOverviewTab: React.FC = () => {
  const limit = 50;
  const [offset, setOffset] = useState(0);
  const [sortBy, setSortBy] = useState<AccessPointOverviewSortField>('deactivatedUntil');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [statusFilter, setStatusFilter] = useState<string[]>([]);

  const [modalEntry, setModalEntry] = useState<AccessPointOverviewEntry | null>(null);
  const [modalAction, setModalAction] = useState<'activate' | 'deactivate' | 'extendBlackout'>('activate');
  const { isOpen, onOpen, onClose } = useDisclosure();

  const { data, loading, refetch } = useQuery<InternalAccessPointOverviewQuery>(GET_ACCESS_POINT_OVERVIEW, {
    variables: {
      limit,
      offset,
      sortBy,
      sortOrder,
      displayStatus: statusFilter.length > 0 ? statusFilter[0] : undefined,
    },
  });

  const handleLoadMore = (page: number) => {
    setOffset((page - 1) * limit);
  };

  const handleSort = (field: AccessPointOverviewSortField) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder(field === 'deactivatedUntil' ? 'asc' : 'desc');
    }
    setOffset(0);
  };

  const renderSortIcon = (field: AccessPointOverviewSortField) => {
    if (sortBy !== field) {
      return <Equals size={16} style={{ display: 'inline', marginLeft: '0.25rem', opacity: 0.4, verticalAlign: 'middle' }} />;
    }
    return sortOrder === 'asc' ? (
      <SortAscending size={16} style={{ display: 'inline', marginLeft: '0.25rem', verticalAlign: 'middle' }} />
    ) : (
      <SortDescending size={16} style={{ display: 'inline', marginLeft: '0.25rem', verticalAlign: 'middle' }} />
    );
  };

  const openActionModal = (entry: AccessPointOverviewEntry, action: 'activate' | 'deactivate' | 'extendBlackout') => {
    setModalEntry(entry);
    setModalAction(action);
    onOpen();
  };

  const totalCount = data?.internalAccessPointOverview.totalCount || 0;

  return (
    <>
      <Flex justifyContent="flex-start" alignItems="center" gap="2" mb="2" flexWrap="wrap">
        <FilterSelectPill
          label="Status"
          options={[
            { label: 'Aktiv', value: 'ACTIVE' },
            { label: 'Inaktiv', value: 'INACTIVE' },
            { label: 'Gekündigt', value: 'CANCELLED' },
          ]}
          initialValues={statusFilter}
          setValues={(values) => {
            setStatusFilter(values);
            setOffset(0);
          }}
          clear={() => {
            setStatusFilter([]);
            setOffset(0);
          }}
        />
      </Flex>

      <Flex justifyContent="space-between" alignItems="center" gap="4" mb="2">
        <Text fontSize="sm" color="gray.500">
          {totalCount} Access Points gefunden
        </Text>
      </Flex>

      <Box>
        <TableContainer>
          <Table variant="striped" size="sm">
            <Thead>
              <Tr>
                <Th>AP ID</Th>
                <Th>Name</Th>
                <Th>PLZ</Th>
                <Th>Stadt</Th>
                <Th>Status</Th>
                <Th cursor="pointer" userSelect="none" onClick={() => handleSort('deactivatedUntil')}>
                  <Flex alignItems="center" gap="2">
                    <span>Blackout endet am</span>
                    {renderSortIcon('deactivatedUntil')}
                  </Flex>
                </Th>
                <Th cursor="pointer" userSelect="none" onClick={() => handleSort('lastDeactivationTimestamp')}>
                  <Flex alignItems="center" gap="2">
                    <span>Zuletzt deaktiviert</span>
                    {renderSortIcon('lastDeactivationTimestamp')}
                  </Flex>
                </Th>
                <Th>Aktionen</Th>
              </Tr>
            </Thead>
            {loading ? null : (
              <Tbody>
                {data?.internalAccessPointOverview.entries.map((entry) => (
                  <Tr key={entry.id}>
                    <Td fontFamily="mono" fontSize="xs">
                      {entry.accessPointID}
                    </Td>
                    <Td>{entry.locationName || '-'}</Td>
                    <Td>{entry.postalCode || '-'}</Td>
                    <Td>{entry.city || '-'}</Td>
                    <Td>{statusBadge(entry.displayStatus)}</Td>
                    <Td>{entry.displayStatus !== 'ACTIVE' && entry.deactivatedUntil ? formatUTCToLocal(entry.deactivatedUntil) : '-'}</Td>
                    <Td>{entry.lastDeactivationTimestamp ? formatUTCToLocal(entry.lastDeactivationTimestamp) : '-'}</Td>
                    <Td>
                      <Flex gap="1">
                        {entry.displayStatus === 'INACTIVE' && (
                          <Tooltip label="Aktivieren" hasArrow>
                            <Button size="xs" colorScheme="green" onClick={() => openActionModal(entry, 'activate')}>
                              Aktivieren
                            </Button>
                          </Tooltip>
                        )}
                        {entry.displayStatus === 'ACTIVE' && (
                          <Tooltip label="Pausieren" hasArrow>
                            <Button size="xs" colorScheme="red" onClick={() => openActionModal(entry, 'deactivate')}>
                              Pausieren
                            </Button>
                          </Tooltip>
                        )}
                        {entry.displayStatus !== 'ACTIVE' && (
                          <Tooltip label="Blackout um 3 Monate verlängern" hasArrow>
                            <Button size="xs" colorScheme="blue" variant="outline" onClick={() => openActionModal(entry, 'extendBlackout')}>
                              +3M
                            </Button>
                          </Tooltip>
                        )}
                      </Flex>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            )}
          </Table>
        </TableContainer>
        {loading ? (
          <Flex width="100%" height="100%" justifyContent="center" alignItems="center" p="12">
            <Spinner />
          </Flex>
        ) : null}
        <Box>
          <Pagination totalAmount={totalCount} amountPerPage={limit} currentPage={offset / limit + 1} handleOnClick={handleLoadMore} />
        </Box>
      </Box>

      <ActionModal isOpen={isOpen} onClose={onClose} refetch={() => refetch()} entry={modalEntry} actionType={modalAction} />
    </>
  );
};
